import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { collection, collectionData, Firestore, limit, orderBy, query, Timestamp, where } from "@angular/fire/firestore";
import { IDailyBonusResponse } from "@soposopo/sopo-models";
import { BehaviorSubject, combineLatest, from, Observable, Subject } from "rxjs";
import { switchMap, map, filter, takeUntil, distinctUntilChanged } from "rxjs/operators";
import { SopoLoadingService } from "src/app/shared/services/sopo-loading/sopo-loading.service";
import { ApiService } from "../backend/api.service";
import { AuthStoreDispatcher } from "../state/auth/auth.dispatcher";
import { ProfileStateDispatcher } from "../state/profile/profile.dispatcher";
import { FeatureService } from "./feature.service";
import { FireSubService } from "./fireSub.service";
import { NumberSuffixPipe } from "src/app/shared/pipes/numberSuffix/number-suffix.pipe";

type GiftAnimationSource = 'homepage-collect-gold' | 'homepage-collect-earn-gold';
@Injectable({
    providedIn: 'root'
})
export class DailyBonusService {
    private _goldUpdateerWithAnimation$: Subject<{ amount: number, fromProfile: boolean, source: GiftAnimationSource, manipulationId: number }> = new Subject();
    private _sweepbuckUpdaterWithAnimation$: Subject<{ amount: number, fromProfile: boolean, source: GiftAnimationSource, manipulationId: number }> = new Subject();
    private _jackpotTicketUpdaterWithAnimation$: Subject<{ amount: number, fromProfile: boolean, source: GiftAnimationSource, manipulationId: number }> = new Subject();
    private _goldChangeDisabled$ = new BehaviorSubject(false);
    private _goldChangeDisableManipulationId = 0;
    private _sweepbuckChangeDisabled$ = new BehaviorSubject(false);
    private _sweepbuckChangeDisableManipulationId = 0;
    private _jackpotTicketChangeDisabled$ = new BehaviorSubject(false);
    private _jackpotTicketChangeDisableManipulationId = 0;
    private renderer: Renderer2;

    constructor(
        private profileDispatcher: ProfileStateDispatcher,
        private firestore: Firestore,
        private loadingCtrl: SopoLoadingService,
        private apiService: ApiService,
        private authDispatcher: AuthStoreDispatcher,
        public featureService: FeatureService,
        private renderFactory: RendererFactory2,
        private fireSubService: FireSubService,
        private numberSuffixPipe: NumberSuffixPipe,
    ) {
        this.renderer = renderFactory.createRenderer(null, null);
    }

    enableGoldChange(manipulationId: number) {
        if (this._goldChangeDisableManipulationId === manipulationId) {
            if (this._goldChangeDisableManipulationId !== 0) {
                this._goldChangeDisableManipulationId = 0;
            }
            this._goldChangeDisabled$.next(false);
        }
    }

    disableGoldChange(manipulationId: number) {
        this._goldChangeDisableManipulationId = manipulationId;
        this._goldChangeDisabled$.next(true);
    }

    enableSweepbuckChange(manipulationId: number) {
        if (this._sweepbuckChangeDisableManipulationId === manipulationId) {
            if (this._sweepbuckChangeDisableManipulationId !== 0) {
                this._sweepbuckChangeDisableManipulationId = 0;
            }
            this._sweepbuckChangeDisabled$.next(false);
        }
    }

    disableSweepbuckChange(manipulationId: number) {
        this._sweepbuckChangeDisableManipulationId = manipulationId;
        this._sweepbuckChangeDisabled$.next(true);
    }

    enableJackpotTicketChange(manipulationId: number) {
        if (this._jackpotTicketChangeDisableManipulationId === manipulationId) {
            if (this._jackpotTicketChangeDisableManipulationId !== 0) {
                this._jackpotTicketChangeDisableManipulationId = 0;
            }
            this._jackpotTicketChangeDisabled$.next(false);
        }
    }

    disableJackpotTicketChange(manipulationId: number) {
        this._jackpotTicketChangeDisableManipulationId = manipulationId;
        this._jackpotTicketChangeDisabled$.next(true);
    }

    getNextBonusDate(){
        return from(this.profileDispatcher.getMyID()).pipe(
            switchMap(userId => {
                const collectionRef = collection(this.firestore, `users/${userId}/shop`);
                const lastBonusGoldQuery = query(
                    collectionRef,
                    where('transactions', '==', 'Daily bonus gold.'),
                    orderBy('timestamp', 'desc'),
                    limit(1)
                );
                return collectionData(lastBonusGoldQuery);
            }),
            takeUntil(this.fireSubService.unsubscriber),
            map(bonusCollection => {
                if (bonusCollection.length === 0) return Date.now();
                const lastBonusDate = <Timestamp>bonusCollection[0]['timestamp'];
                return lastBonusDate.toMillis() + (60 * 60 * 24 * 1000);
            })
        )
    }

    getDailyBonusChecker() {
        return this.getNextBonusDate().pipe(map(nextBonus => {
            const currentDate = Date.now();
            return nextBonus - currentDate <= 0;
        }));
    }

    async getDailyBonusGoldIfAvailable(): Promise<IDailyBonusResponse> {
        try {
            const userId = await this.authDispatcher.getMyID();
            const response = await this.apiService.dailyBonus(userId, this.featureService.sweepbucks);
            return response;
        }
        catch (error) {
            throw error;
        }
    }

    getLoginStreakTimer(): Observable<string> {
        const timerObs = this.apiService.getServerTimeObservable().pipe(map(time => time * 1000));
        const nextStreak = this.profileDispatcher.getNextStreak();
        return combineLatest([timerObs, nextStreak])
            .pipe(
                map(([currentTime, nextStreak]) => {
                    const differenc: number = (nextStreak-currentTime)/1000;
                    if (differenc <= 0 || isNaN(differenc)) return '--:--:--';

                    const hours = Math.floor(differenc / 3600);
                    const minutes = Math.floor(differenc / 60) % 60;
                    const seconds = Math.floor(differenc % 60);

                    const hrs = ('0' + hours).slice(-2);
                    const min = ('0' + minutes).slice(-2);
                    const sec = ('0' + seconds).slice(-2);
                    const countdown = hrs + ':' + min + ':' + sec;
                    return countdown;
                }),
                distinctUntilChanged(),
            )
    }

    getLoginStreak(): Observable<boolean> {
        const timerObs = this.apiService.getServerTimeObservable().pipe(map(time => time * 1000));
        const nextStreak = this.profileDispatcher.getNextStreak();
        return combineLatest([timerObs, nextStreak])
            .pipe(
                map(([currentTime, nextStreak]) => {
                    return nextStreak < currentTime && nextStreak !== 0;
                }),
                distinctUntilChanged(),
                filter(end => end === true),
            )
    }

    getGoldDailyBonusChecker() {
        const timerObs = this.apiService.getServerTimeObservable().pipe(map(time => time * 1000));
        const nextDaily = this.profileDispatcher.getNextDailyBonus();
        return combineLatest([timerObs, nextDaily])
            .pipe(
                map(([currentTime, nextDailyBonus]) => {
                    return nextDailyBonus < currentTime;
                }),
                distinctUntilChanged(),
            )
    }

    animateGift(amount: number, currencyType: CurrencyType, fromProfile = false, source: GiftAnimationSource = 'homepage-collect-gold', manipulationId = 0) {   
        switch(currencyType){
            case 'gold': {
                this._goldUpdateerWithAnimation$.next({amount, fromProfile, source, manipulationId});
                break;
            }
            case 'sweepbuck': {
                this._sweepbuckUpdaterWithAnimation$.next({amount, fromProfile, source, manipulationId});
                break;
            }
            case 'jackpotTicket': {
                this._jackpotTicketUpdaterWithAnimation$.next({amount, fromProfile, source, manipulationId});
                break;
            }
        }
    }

    get goldAnimatorObs(): Observable<IGiftAnimationObservableResult> {
        return this._goldUpdateerWithAnimation$.asObservable().pipe(
            filter(updater => updater.fromProfile || updater.amount > 0),
            map(updater => { return { amount: updater.amount, source: updater.source, manipulationId: updater.manipulationId }}),
        );
    }

    get sweepbuckAnimatorObs(): Observable<IGiftAnimationObservableResult> {
        return this._sweepbuckUpdaterWithAnimation$.asObservable().pipe(
            filter(updater => updater.fromProfile || updater.amount > 0),
            map(updater => { return { amount: updater.amount, source: updater.source, manipulationId: updater.manipulationId }}),
        );
    }

    get jackpotAnimatorObs(): Observable<IGiftAnimationObservableResult> {
        return this._jackpotTicketUpdaterWithAnimation$.asObservable().pipe(
            filter(updater => updater.fromProfile || updater.amount > 0),
            map(updater => { return { amount: updater.amount, source: updater.source, manipulationId: updater.manipulationId }}),
        );
    }

    get goldChangeDisabledObs() {
        return this._goldChangeDisabled$.asObservable();
    }

    get sweepbuckChangeDisabledObs() {
        return this._sweepbuckChangeDisabled$.asObservable();
    }

    get jackpotTicketChangeDisabledObs() {
        return this._jackpotTicketChangeDisabled$.asObservable();
    }

    sleep(duration: number) {
        return new Promise<void>(resolve => setTimeout(resolve, duration));
    }

    async animateText(addAmount: number, undefinedFlag = false, old: string, parent: string, animationAndGoldChangeDisable = true) {
        if (!undefinedFlag) return;

        const oldLable = document.getElementById(old) as HTMLElement;
        if (!animationAndGoldChangeDisable && oldLable) {
            oldLable.innerText = this.numberSuffixPipe.transform(addAmount);
            return;
        }
        const currency = document.getElementById(parent) as HTMLElement;

        const scaleCurrency = currency.offsetWidth / currency.getBoundingClientRect().width;

        const newGoldLabel = this.renderer.createElement('label') as HTMLElement;
        this.renderer.addClass(newGoldLabel, old);
        this.renderer.setAttribute(newGoldLabel, 'id', old);
        this.renderer.setStyle(newGoldLabel, 'opacity', '0');
        this.renderer.appendChild(currency, newGoldLabel);

        oldLable.style.transform = `translateY(-100%)`;
        oldLable.style.transition = `transform 1s cubic-bezier(.22,.34,.9,.88), opacity 0.5s`
        oldLable.style.opacity = '0';
        await this.sleep(100);
        this.renderer.removeChild(currency, oldLable);

        const newGoldText = this.renderer.createText(this.numberSuffixPipe.transform(addAmount));
        this.renderer.appendChild(newGoldLabel, newGoldText);

        const translateValue = this.calculateTranslate(newGoldLabel, currency, scaleCurrency);

        newGoldLabel.style.transform = `${translateValue}`;
        newGoldLabel.style.transition = `transform 0.1s cubic-bezier(.22,.34,.9,.88), opacity 0.6s`
        newGoldLabel.style.opacity = '1';
        await this.sleep(200);
    }

    async animateFlyingGift(sourceElement: string, targetElement: string, giftType: CurrencyType) {
        const target = document.getElementById(targetElement);
        const source = document.getElementById(sourceElement);
        const animSource = document.getElementById('sopo-app');
    
        const endPosition = this.getCenterOfElement(target);
        const startPosition = this.getCenterOfElement(source);
    
        const scaleFactorTarget = target.offsetWidth / target.getBoundingClientRect().width;
        const scaleFactorSource = source.offsetWidth / source.getBoundingClientRect().width;
    
        const sourceSize = { height: source.getBoundingClientRect().height * scaleFactorSource };
        const targetSize = { height: target.getBoundingClientRect().height * scaleFactorTarget };
    
        const duration = 0.5; // in seconds
        const giftCount = 5;
        const imgStyle = {
            zIndex: '10',
            height: `${sourceSize.height * 2}px`,
            position: 'absolute',
            transition: `top ${duration}s, left ${duration}s ease-in-out, width ${duration}s ease-out, height ${duration}s ease-out, opacity 0.2s`,
            opacity: '0',
            top: `${startPosition.y - (sourceSize.height * 2) / 2}px`,
            left: `${startPosition.x - (sourceSize.height * 2) / 2}px`
        };

        const giftElements: any[] = [];
        for (let i = 0; i <= giftCount; i++) {
            const giftImg = this.renderer.createElement('img');

            // Setting image source based on gift type
            if (giftType === 'gold') {
                this.renderer.setAttribute(giftImg, 'src', '/assets/icon/chips/gold_chip.webp');
            } else if (giftType === 'sweepbuck') {
                this.renderer.setAttribute(giftImg, 'src', '/assets/img/sweep/money.webp');
            } else if (giftType === 'jackpotTicket') {
                this.renderer.setAttribute(giftImg, 'src', '/assets/img/sweep/gold_ticket.webp');
            }

            this.setMultipleStyles(imgStyle, giftImg);
            this.renderer.appendChild(animSource, giftImg);
            giftElements.push(giftImg);
        }

        await this.sleep(50);

        const movedStyles = {
            height: `${targetSize.height}px`,
            opacity: '1',
            top: `${endPosition.y - targetSize.height / 2}px`,
            left: `${endPosition.x - targetSize.height / 2}px`
        };

        for (let giftImg of giftElements) {
            this.setMultipleStyles(movedStyles, giftImg);
            await this.sleep(50);
        }

        await this.sleep(duration * 1000);

        for (let giftImg of giftElements) {
            this.renderer.removeChild(animSource, giftImg);
        }
    }

    setMultipleStyles(style: {[key: string]: string}, element){
        Object.keys(style).forEach(styleKey => {
            this.renderer.setStyle(element, styleKey, style[styleKey]);
        })
    }

    getCenterOfElement(element) {
        const rect: DOMRect = element.getBoundingClientRect();
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        }
    }

    private calculateTranslate(source: HTMLElement, target: HTMLElement, scaleFactor: number): string {
        const sourceClientRect = source.getBoundingClientRect();
        const targetClientRect = target.getBoundingClientRect();
        const translateY = (targetClientRect.y + targetClientRect.height / 2) - (sourceClientRect.y + sourceClientRect.height / 2);

        return `translate(${translateY * scaleFactor}px)`;
    }
}

export interface IGiftAnimationObservableResult {
    amount: number;
    source: GiftAnimationSource;
    manipulationId: number;
}

export type CurrencyType = 'gold' | 'sweepbuck' | 'jackpotTicket';
